import { useEffect, useState } from "react"
import { Check, Pencil, X, Sparkles } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import type { BidBriefStructured } from "@/stores/strategy-store"

interface BidBriefConvertNotesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  proposed: BidBriefStructured | null
  onApply: (accepted: Partial<BidBriefStructured>) => void
}

interface FieldDef {
  key: keyof BidBriefStructured
  label: string
}

const FIELDS: FieldDef[] = [
  { key: 'strategicNarrative', label: 'Strategic narrative' },
  { key: 'clientPriorities', label: 'Client priorities' },
  { key: 'risksAndWatchouts', label: 'Risks & watchouts' },
  { key: 'voiceAndPosture', label: 'Voice & posture' },
  { key: 'evaluatorLens', label: 'Evaluator lens' },
]

type FieldState = 'accept' | 'edit' | 'skip'

export function BidBriefConvertNotesDialog({
  open,
  onOpenChange,
  proposed,
  onApply,
}: BidBriefConvertNotesDialogProps) {
  const [states, setStates] = useState<Record<keyof BidBriefStructured, FieldState>>({} as Record<keyof BidBriefStructured, FieldState>)
  const [edits, setEdits] = useState<Partial<BidBriefStructured>>({})

  // Reset state when proposed changes
  useEffect(() => {
    if (!proposed) return
    const initial = {} as Record<keyof BidBriefStructured, FieldState>
    for (const field of FIELDS) {
      initial[field.key] = proposed[field.key] ? 'accept' : 'skip'
    }
    setStates(initial)
    setEdits({ ...proposed })
  }, [proposed])

  if (!proposed) return null

  const acceptedCount = FIELDS.filter(f => states[f.key] !== 'skip' && (edits[f.key] ?? proposed[f.key])).length

  const handleSetState = (key: keyof BidBriefStructured, state: FieldState) => {
    setStates(s => ({ ...s, [key]: state }))
  }

  const handleEdit = (key: keyof BidBriefStructured, value: string) => {
    setEdits(e => ({ ...e, [key]: value }))
  }

  const handleApply = () => {
    const accepted: Partial<BidBriefStructured> = {}
    for (const field of FIELDS) {
      if (states[field.key] === 'skip') continue
      const value = edits[field.key] ?? proposed[field.key]
      if (value && value.trim()) {
        accepted[field.key] = value.trim()
      }
    }
    onApply(accepted)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-[hsl(var(--ai-accent-soft))] flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-[hsl(var(--ai-accent-text))]" />
            </div>
            <div className="flex-1">
              <DialogTitle>Organise notes into fields</DialogTitle>
              <DialogDescription className="mt-0.5">
                AI extracted these from your notes. Accept, edit, or skip each — your notes stay untouched.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2.5 pr-1">
          {FIELDS.map(field => {
            const state = states[field.key] ?? 'skip'
            const proposedValue = proposed[field.key]
            const editedValue = edits[field.key]
            const displayValue = editedValue ?? proposedValue

            const stateClass = state === 'accept'
              ? 'border-[hsl(var(--brand-soft))] bg-[hsl(var(--brand-soft))]/30'
              : state === 'edit'
              ? 'border-[hsl(var(--ai-accent-soft))] bg-[hsl(var(--ai-accent-soft))]/40'
              : 'opacity-60'

            return (
              <div key={field.key} className={cn("border rounded-lg p-3 transition-colors", stateClass)}>
                <div className="flex items-center gap-2 mb-1.5">
                  <StatePill state={state} />
                  <span className="text-sm font-medium">{field.label}</span>
                </div>

                {!proposedValue ? (
                  <div className="text-xs italic text-muted-foreground">
                    Notes don't address this field — leaving empty.
                  </div>
                ) : state === 'edit' ? (
                  <Textarea
                    value={editedValue ?? proposedValue}
                    onChange={(e) => handleEdit(field.key, e.target.value)}
                    rows={3}
                    className="text-sm"
                  />
                ) : (
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{displayValue}</div>
                )}

                {proposedValue && (
                  <div className="flex gap-1.5 mt-2">
                    <Button
                      size="sm"
                      variant={state === 'accept' ? 'default' : 'ghost'}
                      className="h-7 text-xs"
                      onClick={() => handleSetState(field.key, 'accept')}
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant={state === 'edit' ? 'default' : 'ghost'}
                      className="h-7 text-xs"
                      onClick={() => handleSetState(field.key, 'edit')}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant={state === 'skip' ? 'default' : 'ghost'}
                      className="h-7 text-xs"
                      onClick={() => handleSetState(field.key, 'skip')}
                    >
                      <X className="h-3 w-3 mr-1" />
                      Skip
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <DialogFooter className="border-t border-border/60 pt-3 flex-row sm:justify-between sm:items-center">
          <span className="text-meta">
            Notes preserved · Brief becomes <span className="font-medium text-foreground">Mixed · draft</span>
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" onClick={handleApply} disabled={acceptedCount === 0}>
              Apply {acceptedCount} {acceptedCount === 1 ? 'field' : 'fields'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function StatePill({ state }: { state: FieldState }) {
  if (state === 'accept') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-hover))]">
        <Check className="h-2.5 w-2.5" />
        Will accept
      </span>
    )
  }
  if (state === 'edit') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[hsl(var(--ai-accent-soft))] text-[hsl(var(--ai-accent-text))]">
        <Pencil className="h-2.5 w-2.5" />
        Editing
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground">
      <X className="h-2.5 w-2.5" />
      Skipped
    </span>
  )
}
